import {
  BrowserRouter,
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
} from 'react-router-dom';
import 'react-toastify/dist/ReactToastify.css';
import '../config/i18n';

import {
  APP_PATHS,
  AppletSelector,
  CompleteInvitation,
  DownloadUSDocuments,
  ForgotPassword,
  LeadRegister,
  LinkSchool,
  Login,
  ManipulativeEnvironment,
  NotificationsProvider,
  PasswordExpirationOverlay,
  PrintGuides,
  PrintingTool,
  PrintMockExams,
  PrintPacingGuide,
  PrintPersonalizedPractice,
  ReportsFullScreenProvider,
  ResetPassword,
  TestReportView,
  useAppHeight,
  useUser,
} from '@innovamat/ga-features';
import { SnackBarContainer } from '@innovamat/glimmer-components';
import { useUpdateEffect } from '@innovamat/hooks';
import { inlineManual, storage } from '@innovamat/radiance-utils';
import { RegionalConfigVariablesProvider } from '@innovamat/regional-config-variables';

import { ENVIRONMENT, GEOLOCATION_API } from '../utils/variables';
import { AdminClassroomNavigationWrapper } from './components/admin-classroom-navigation-wrapper';
import { AdminNavigationWrapper } from './components/admin-navigation-wrapper';
import { ClassroomNavigationWrapper } from './components/classroom-navigation-wrapper';
import { CourseNavigationWrapper } from './components/course-navigation-wrapper';
import { DigitalPracticeNavigationWrapper } from './components/digital-practice-navigation-wrapper';
import { EmailPreferencesPage } from './components/email-preferences-page';
import { InitDatadog } from './components/init-datadog';
import { InitScripts } from './components/init-scripts';
import { JourneysNavigationWrapper } from './components/journeys-navigation-wrapper';
import { LocaleManager } from './components/locale-manager';
import { Maintenance } from './components/maintenance';
import { ProtectedAdminRoute } from './components/protected-admin-route';
import { ProtectedRoute } from './components/protected-route';
import { Providers } from './components/providers';
import { ResourceRouter } from './components/resource-router';
import { StudentViewManagement } from './components/student-view-management';
import { CourseSelectorPage } from './pages/course-selector-page';
import { FamilyPaymentsTeachersPage } from './pages/family-payments-teachers-page';
import { JoinToClassroomPage } from './pages/join-to-classroom-page';
import { LandingPage } from './pages/landing-page';
import { SettingsPage } from './pages/settings-page';

storage.initRedirectIfTokenIsInvalid({ redirectUrl: '/' });

function HomeRoute(): JSX.Element {
  const { isLoggedIn } = useUser();

  if (!isLoggedIn) return <Login />;

  return (
    <ProtectedRoute>
      <PasswordExpirationOverlay />
      <LandingPage />
    </ProtectedRoute>
  );
}

function UrlTracker(): null {
  const location = useLocation();

  useUpdateEffect(() => {
    inlineManual.updateInlinePlayer();
  }, [location]);

  return null;
}

function RouteWithNotifications(): JSX.Element {
  return (
    <NotificationsProvider>
      <Outlet />
    </NotificationsProvider>
  );
}

export function ManagerRouting(): JSX.Element {
  useAppHeight();

  return (
    <BrowserRouter>
      <RegionalConfigVariablesProvider
        environment={ENVIRONMENT || 'local'}
        geolocationAPI={GEOLOCATION_API}
      >
        <Providers>
          <SnackBarContainer autoClose={3000} closeButton={false} />
          <LocaleManager />
          <StudentViewManagement />
          <InitDatadog />
          <InitScripts />
          <UrlTracker />

          <Routes>
            <Route path={APP_PATHS.MAINTENANCE} element={<Maintenance />} />

            <Route
              path={`/:lng${APP_PATHS.FORGOT_PASSWORD_URL}`}
              element={<ForgotPassword />}
            />
            <Route
              path={`/:lng${APP_PATHS.RESET_PASSWORD_URL}`}
              element={<ResetPassword />}
            />
            <Route
              path={`/:lng${APP_PATHS.LEAD_REGISTER_URL}`}
              element={<LeadRegister />}
            />
            <Route
              path={`/:lng${APP_PATHS.COMPLETE_USER_INVITATION_URL}`}
              element={<CompleteInvitation />}
            />
            <Route
              path={`/:lng${APP_PATHS.COMPLETE_PARENT_INVITATION_URL}`}
              element={<CompleteInvitation />}
            />
            <Route
              path={`/:lng${APP_PATHS.LINK_SCHOOL_URL}`}
              element={<LinkSchool />}
            />

            <Route path={APP_PATHS.LANDING_PAGE_URL} element={<HomeRoute />} />

            <Route
              path={`${APP_PATHS.PRINTING_TOOL_URL}`}
              element={
                <ProtectedRoute>
                  <PrintingTool />
                </ProtectedRoute>
              }
            />

            <Route
              path={`${APP_PATHS.PREFERENCES_URL}/:encodedMail`}
              element={<EmailPreferencesPage />}
            />

            <Route path={APP_PATHS.LANDING_PAGE_URL} element={<HomeRoute />} />

            {/* PROTECTED ROUTES WITHOUT OVERLAY */}
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <Outlet />
                </ProtectedRoute>
              }
            >
              <Route path={APP_PATHS.SETTINGS_URL} element={<SettingsPage />} />
              <Route
                path={APP_PATHS.MANIPULATIVE_URL}
                element={<ManipulativeEnvironment />}
              />
              <Route
                path={APP_PATHS.APPLETS_URL}
                element={<AppletSelector />}
              />

              <Route
                path={`${APP_PATHS.APPLET_BY_SCENE_URL}/:scene/:pack`}
                element={<AppletSelector />}
              />
            </Route>

            {/* PROTECTED ROUTES WITH OVERLAY */}
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <PasswordExpirationOverlay />
                  <Outlet />
                </ProtectedRoute>
              }
            >
              <Route
                path={APP_PATHS.JOIN_TO_CLASSROOM_URL}
                element={<JoinToClassroomPage />}
              />

              {/* COURSE SELECTOR */}
              <Route
                path={`${APP_PATHS.COURSE_SELECTOR_BASE_URL}/:navType`}
                element={
                  <ReportsFullScreenProvider>
                    <CourseSelectorPage />
                  </ReportsFullScreenProvider>
                }
              />

              {/*TEACHER FAMILY PAYMENTS */}
              <Route
                path={APP_PATHS.FAMILY_PAYMENTS_URL}
                element={<FamilyPaymentsTeachersPage />}
              />

              {/* CLASSROOM NAVIGATION */}
              <Route
                path={`${APP_PATHS.CLASSROOM_URL}/:classroomId`}
                element={<RouteWithNotifications />}
              >
                <Route
                  path=":menu?/:submenu?/page"
                  element={
                    <ReportsFullScreenProvider>
                      <ClassroomNavigationWrapper />
                    </ReportsFullScreenProvider>
                  }
                />
                <Route path=":menu/:type/:id" element={<ResourceRouter />} />
                <Route path=":menu/print/:ids" element={<PrintGuides />} />

                {/* PRINT TESTS AND RESULTS */}
                <Route
                  path="print-mock-exams/:testRoundId/:classroomName/:organizationName"
                  element={<PrintMockExams />}
                />
              </Route>

              {/* JOURNEYS NAVIGATION */}
              <Route
                path={`${APP_PATHS.JOURNEYS_URL}/:journeyId`}
                element={<RouteWithNotifications />}
              >
                <Route
                  path=":menu?/:submenu?/page"
                  element={<JourneysNavigationWrapper />}
                />
                <Route path=":menu/:type/:id" element={<ResourceRouter />} />
              </Route>

              {/* PRINT GUIDES */}
              <Route path="/:menu/print/:ids" element={<PrintGuides />} />

              {/* TEST REPORT */}
              <Route
                path="/test-report/:testRoundId"
                element={<TestReportView />}
              />

              {/* PRINT US MODULES */}
              <Route
                path="/print-us-modules/:courseId"
                element={<PrintPersonalizedPractice />}
              />

              {/* PRINT US PACING GUIDE */}
              <Route
                path="/print-pacing-guide/:courseId"
                element={<PrintPacingGuide />}
              />

              {/* DOWNLOAD US DOCUMENTS */}
              <Route
                path="/download-us-documents"
                element={<DownloadUSDocuments />}
              />

              {/* COURSE NAVIGATION */}
              <Route path={`${APP_PATHS.COURSE_URL}/:courseId`}>
                <Route path="applets" element={<AppletSelector />} />
                <Route
                  path=":menu?/:submenu?/page"
                  element={<CourseNavigationWrapper />}
                />
                <Route path=":menu/print/:ids" element={<PrintGuides />} />
                <Route path=":menu/:type/:id" element={<ResourceRouter />} />
              </Route>

              {/* DIGITAL PRACTICE */}
              <Route path={`${APP_PATHS.DIGITAL_PRACTICE_URL}/:classroomId`}>
                <Route
                  path=":menu?/:submenu?/page"
                  element={<DigitalPracticeNavigationWrapper />}
                />
                <Route path=":menu/:type/:id" element={<ResourceRouter />} />
              </Route>

              {/* ADMIN NAVIGATION */}
              <Route
                path={`${APP_PATHS.ORGANIZATION_URL}/:organizationId`}
                element={
                  <ProtectedAdminRoute>
                    <Outlet />
                  </ProtectedAdminRoute>
                }
              >
                <Route
                  path="classrooms/:classroomId/:menu?/:submenu?/page"
                  element={
                    <ReportsFullScreenProvider>
                      <AdminClassroomNavigationWrapper />
                    </ReportsFullScreenProvider>
                  }
                />
                <Route
                  path=":menu?/:submenu?"
                  element={<AdminNavigationWrapper />}
                />
              </Route>
            </Route>

            {/* PUBLIC RESOURCES */}
            <Route path="/:menu/:type/:id" element={<ResourceRouter />} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Providers>
      </RegionalConfigVariablesProvider>
    </BrowserRouter>
  );
}

export default ManagerRouting;
